/**
 * <s-mega-menu> — the « Boutique » disclosure in the desktop header.
 *
 * Markup contract (see snippets/mm-mega-menu.liquid):
 *   <s-mega-menu id="…">
 *     <button commandfor="…" command="--toggle" aria-expanded aria-controls>…</button>
 *     <div data-mega-panel id="…">…links…</div>
 *   </s-mega-menu>
 *
 * Opens on hover (with a short intent delay), on click/tap via the --toggle command,
 * and with ArrowDown from the trigger. Escape closes and returns focus to the trigger;
 * moving focus or clicking outside closes. The open state is the `data-open` attribute,
 * styled with CSS only (transitions are disabled under prefers-reduced-motion by the markup).
 */
class SMegaMenu extends HTMLElement {
  #abort = null;
  #openTimer = 0;
  #closeTimer = 0;
  #hoverOpenedAt = 0;

  get #trigger() {
    return this.querySelector(':scope > button[aria-controls]');
  }

  get #panel() {
    return this.querySelector('[data-mega-panel]');
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.addEventListener('command', (event) => {
      if (event.command !== '--toggle') return;
      // A click right after a hover-open keeps the menu open instead of flickering closed.
      if (this.hasAttribute('data-open') && Date.now() - this.#hoverOpenedAt < 400) return;
      this.toggle(!this.hasAttribute('data-open'));
    }, { signal });

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !hoverable.matches) return;
      clearTimeout(this.#closeTimer);
      this.#openTimer = window.setTimeout(() => {
        if (!this.hasAttribute('data-open')) this.#hoverOpenedAt = Date.now();
        this.toggle(true);
      }, 90);
    }, { signal });

    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.#openTimer);
      this.#closeTimer = window.setTimeout(() => this.toggle(false), 220);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('data-open')) {
        event.preventDefault();
        event.stopPropagation();
        this.toggle(false);
        this.#trigger?.focus();
      } else if (event.key === 'ArrowDown' && event.target === this.#trigger) {
        event.preventDefault();
        this.toggle(true);
        this.#panel?.querySelector('a[href]')?.focus();
      }
    }, { signal });

    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.toggle(false);
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      if (this.hasAttribute('data-open') && !this.contains(event.target)) this.toggle(false);
    }, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
  }

  toggle(open) {
    this.toggleAttribute('data-open', open);
    this.#trigger?.setAttribute('aria-expanded', String(open));
    const panel = this.#panel;
    if (panel) panel.inert = !open;
  }
}

if (!customElements.get('s-mega-menu')) {
  customElements.define('s-mega-menu', SMegaMenu);
}
